import { useFormik } from "formik";
import type React from "react";
import { useTranslation } from "react-i18next";

import { useInviteManagement } from "hooks/state/useInviteManagement";
import { useProduct } from "hooks/useProduct";

import { CssFlex, CssIcon } from "styles/helpers/layout";

import { useState } from "react";
import {
  type UpdateInviteLinkUsageLimitFormFields,
  buildUpdateInviteLinkUsageLimitFormSchema,
} from "./UpdateInviteLinkUsageLimitForm.schema";
import {
  StyledEditButton,
  StyledMaxUsageInput,
  StyledNarrowButton,
} from "./UpdateInviteLinkUsageLimitForm.styled";

interface UpdateInviteLinkUsageLimitFormProps {
  inviteLinkId: string;
  currentUsage: number;
  currentMaxUsageLimit: number;
}

export const UpdateInviteLinkUsageLimitForm: React.FC<UpdateInviteLinkUsageLimitFormProps> = ({
  inviteLinkId,
  currentUsage,
  currentMaxUsageLimit,
}) => {
  const { t } = useTranslation("modal");

  const { fetchInvitationBundleList } = useProduct();
  const { updateInvitationLink } = useInviteManagement();
  const [showMaxUsageLimitEditForm, setShowMaxUsageLimitEditForm] = useState(false);

  const formik = useFormik<UpdateInviteLinkUsageLimitFormFields>({
    initialValues: {
      maxUsageLimit: currentMaxUsageLimit,
    },
    validationSchema: buildUpdateInviteLinkUsageLimitFormSchema({ currentUsage }),
    validateOnChange: true,
    onReset: () => {
      setShowMaxUsageLimitEditForm(false);
    },
    onSubmit: (values) => {
      updateInvitationLink(inviteLinkId, { maxUsageLimit: values.maxUsageLimit }).then(
        (wasUpdateSuccessful) => {
          if (wasUpdateSuccessful) {
            // Refresh invite list after successful update
            fetchInvitationBundleList();

            // Switch back to read-only mode after successful edit
            formik.resetForm();
            setShowMaxUsageLimitEditForm(false);
          }
        },
      );
    },
  });

  // Show current usage limit and button to toggle form
  if (!showMaxUsageLimitEditForm) {
    return (
      <CssFlex flexDirection="row" alignItems="center" gap={0.75}>
        <span data-cy="invite-link-max-usage-limit">{currentMaxUsageLimit}</span>
        <StyledEditButton
          aria-label={t(
            "modal:createLinkInviteModal.steps.linkDetails.maxUsageEditForm.editButton",
          )}
          dataCy="edit-usage-limit-button"
          variant="text"
          onClick={() => setShowMaxUsageLimitEditForm(true)}
        >
          <CssIcon iconName="fas fa-pencil" />
        </StyledEditButton>
      </CssFlex>
    );
  }

  // Show form to update usage limit
  return (
    <CssFlex flexDirection="row" alignItems="center" gap={0.75}>
      <StyledMaxUsageInput
        aria-label={t("modal:createLinkInviteModal.steps.linkDetails.details.maxUsageLimit")}
        name="maxUsageLimit"
        value={formik.values.maxUsageLimit}
        type="number"
        data-cy="invite-link-max-usage-limit-input"
        min={currentUsage}
        onChange={formik.handleChange}
      />
      <StyledNarrowButton
        dataCy="confirm-usage-limit-update-button"
        variant="primary"
        type="submit"
        disabled={formik.isSubmitting || !formik.isValid || !formik.dirty}
        onClick={() => formik.submitForm()}
      >
        {t("modal:createLinkInviteModal.steps.linkDetails.maxUsageEditForm.saveButton")}
      </StyledNarrowButton>
      <StyledNarrowButton
        dataCy="cancel-usage-limit-update-button"
        variant="error"
        type="reset"
        onClick={() => formik.resetForm()}
      >
        {t("modal:createLinkInviteModal.steps.linkDetails.maxUsageEditForm.cancelButton")}
      </StyledNarrowButton>
    </CssFlex>
  );
};
